import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import {
    ArrowBackRoundIcon,
    CircleGreenCircle,
    LoadingIcon,
    WhiteArrowDropDown,
} from "asset/icons";
import axios from "axios";
import PaymentTab from "component/flex/shipments/tabs/PaymentTab";
import Messages from "component/flex/message/Messages";
import PurchaseOrderTab from "component/flex/shipments/tabs/PurchaseOrderTab";
import MoreOptionsDropdown from "component/flex/shipments/modals/MoreOptionsDropdown";
import MakePaymentModal from "component/flex/shipments/modals/MakePaymentModal";
import EditShipperModal from "component/flex/shipments/modals/EditShipperModal";
import DocumentTab from "component/flex/shipments/tabs/DocumentTab";
import ActivityTab from "component/flex/shipments/tabs/ActivityTab";
import getInitialsMessage from "utilities/messages/getInitialsMessage";
import StatusTracking from "component/flex/shipments/StatusTracking";
import Loading from "component/loading";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
    handleChangeShipmentStatus,
    handleDuplicateShipment,
    handleGetAllShipments,
    handleMarkShipperPayment,
    handleReceiverAcceptOrReject,
} from "api/fetchers";
import { shipmentStatus, SOCKET_EVENTS, SOCKET_LISTENERS } from "constants/global";
import UpdateStatusModal from "component/flex/shipments/components/modals/UpdateStatusModal";
import { useSelector } from "react-redux";
import useGetRequest from "hooks/useGetRequest";
import Swal from "sweetalert2";
import RecordPayment from "component/flex/RecordPayment";
import CancelShipmentModal from "component/flex/shipments/components/modals/CancelShipmentModal";
import UploadDocModal from "component/form/uploadDocModal";
import { LuLoader } from "react-icons/lu";
import { WorldMap } from "component/flex/shipments/WorldMap";
import { toast } from "react-toastify";
import RejectShipmentModal from "component/flex/shipments/components/modals/RejectShipmentModal";
import AcceptShipmentModal from "component/flex/shipments/components/modals/AcceptShipmentModal";
import RatingModal from "dashboard/component/rating";
import useUploadDoc from "hooks/useUploadDoc";
import useSocket from "hooks/useSocket";
import { cn } from "lib/utils";

dayjs.extend(advancedFormat);

const tabs = ["Payments", "Purchase Orders", "Documents", "Messages", "Activity"];

const ShipmentShow = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const shipmentId = searchParams.get("shipmentId");
    const isAssignedView = searchParams.get("userView");
    const [activeTab, setActiveTab] = useState("Payments");
    const [makePaymentModal, setMakePaymentModal] = useState(false);
    const [selectedShipper, setSelectedShipper] = useState(null);
    const [showEditShipper, setShowEditShipper] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showRating, setShowRating] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showAcceptModal, setShowAcceptModal] = useState(false);
    const business = useSelector((state) => state.business);
    const [fetchActivities, activities] = useGetRequest();
    const [selectedShipment, setSelectedShipment] = useState(null);
    const [cancelShipmentModal, setShowCancelShipmentModal] = useState(false);
    const [recordPaymentModal, setRecordPaymentModal] = useState(false);
    const queryClient = useQueryClient();
    const [itemIdToLoad, setItemIdToLoad] = useState(null);
    const [messageList, setMessageList] = useState([]);
    const { socket } = useSocket();
    const [convoDetails, setConvoDetails] = useState(null);

    const {
        data,
        isLoading: isLoadingShipment,
        refetch: refetchShipmentData,
    } = useQuery({
        queryFn: () => handleGetAllShipments({ endpoint: shipmentId }),
        queryKey: [shipmentId, "single-shipment"],
    });

    const handleAction = (actionName, shipment) => {
        // TODO: Implement functionality for each action
        if (actionName === "Record Payment") {
            setRecordPaymentModal(true);
        }
    };

    useEffect(() => {
        if (sessionStorage.getItem("showShipmentRating") === "true") {
            setTimeout(() => {
                setShowRating(true);
            }, 3000);
            sessionStorage.removeItem("showShipmentRating");
        }
    }, []);
    const { setAddDocs, addDocs, handleUpload, uploading } = useUploadDoc({
        shipmentId,
        refetch: refetchShipmentData,
    });

    const { mutateAsync: handleDuplicateFunc, isLoading: isDuplicatingShipment } = useMutation({
        mutationFn: (shipmentId) => {
            handleDuplicateShipment(shipmentId);
        },
        mutationKey: "duplicate-shipment",
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ["get-all-shipments"] });
            navigate("/flex/home/shipments");
            console.log({ data }, "after successful duplication");
        },
        onError: (error) => {
            console.log({ error }, "error duplicating shipment");
            toast.error(error?.message || "Error duplicating shipment");
        },
    });

    const {
        mutateAsync: handleReceiverRejectOrAcceptFunc,
        isLoading: isLoadingReceiversRejectOrApprove,
    } = useMutation({
        mutationFn: ({ shipmentId, status }) => {
            handleReceiverAcceptOrReject({ shipmentId, status });
        },
        mutationKey: "accept-reject-shipment",
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ["get-all-shipments"] });
            console.log({ data }, "after successful duplication");
        },
        onError: (error) => {
            console.log({ error }, "error duplicating shipment");
            toast.error(error?.message || "Error duplicating shipment");
        },
    });

    const { mutateAsync: handleRejectApproveFunc, isLoading: isLoadingShipperRejectOrApprove } =
        useMutation({
            mutationFn: ({ shipmentId, status }) => {
                handleReceiverAcceptOrReject({ shipmentId, status });
            },
            mutationKey: "accept-reject-shipment",
            onSuccess: (data) => {
                queryClient.invalidateQueries({ queryKey: ["get-all-shipments"] });
                console.log({ data }, "after successful duplication");
            },
            onError: (error) => {
                console.log({ error }, "error duplicating shipment");
                toast.error(error?.message || "Error duplicating shipment");
            },
        });

    const handleCheckIfCancelled = (shipment) => {
        if (shipment?.shipmentStatus === "Cancelled") {
            toast.error("You cannot perform this action on a cancelled shipment");
            return false;
        }
        return true;
    };

    const shipmentOptions = (shipment) => [
        {
            label: "Record Payment",
            value: "record-payment",
            action: () => {
                if (handleCheckIfCancelled(shipment)) {
                    setSelectedShipment(shipment);
                    handleAction("Record Payment", shipment);
                }
            },
        },
        {
            label: "Add document",
            value: "add-document",
            action: () => {
                if (handleCheckIfCancelled(shipment)) {
                    setAddDocs(true);
                    handleAction("Update Document", shipment);
                }
            },
        },
        {
            label: "Edit Shipment",
            value: "edit-shipment",
            action: () => {
                if (handleCheckIfCancelled(shipment)) {
                    navigate(`/flex/shipments/create?edit=${shipment?._id}`, {
                        state: { shipmentData: shipment },
                    });
                }
            },
        },
        {
            label: "Duplicate this Shipment",
            value: "duplicate-shipment",
            action: () => {
                if (handleCheckIfCancelled(shipment)) {
                    handleDuplicateFunc(shipment?._id);
                }
            },
        },
        {
            label: "Cancel Shipment",
            value: "cancel-shipment",
            className: "text-red-500",
            action: () => {
                if (handleCheckIfCancelled(shipment)) {
                    setSelectedShipment(shipment);
                    setShowCancelShipmentModal(true);
                }
            },
        },
    ];

    const { mutateAsync: markAsPaidFunc, isLoading: loadingShipperFunc } = useMutation({
        mutationFn: ({ status, id }) => handleMarkShipperPayment(status, id, shipmentId),
        mutationKey: [shipmentId],
        onSuccess: (data) => {
            if (data?.status) {
                Swal.fire({
                    text: data?.response?.data?.message,
                    icon: "error",
                    confirmButtonText: "Close",
                    width: "280px",
                    confirmButtonColor: "crimson",
                });
            } else {
                refetchShipmentData();
            }
        },
    });

    // const { mutateAsync: uploadDocFunc } = useMutation({
    //     mutationFn: ({ link, name, originalFilename }) =>
    //         handleUploadShipmentDoc({ link, name, originalFilename, shipmentId }),
    //     mutationKey: [shipmentId, "upload-document"],
    //     onSuccess: (data) => {
    //         if (data) {
    //              refetchShipmentData();
    //              Swal.fire({
    //                  title: "Success",
    //                  text: data?.data?.message || data?.message || "File updated successfully",
    //                  icon: "success",
    //                  confirmButtonText: "OK",
    //                  width: "280px",
    //                  confirmButtonColor: "rgb(111, 0, 255)",
    //              });
    //              setAddDocs(false);
    //         } else {
    //             Swal.fire({
    //                title: "Error",
    //                text: data?.data?.message,
    //                icon: "error",
    //                confirmButtonText: "Close",
    //                width: "280px",
    //                confirmButtonColor: "crimson",
    //            });
    //         }
    //     },
    // });

    const singleShipment = data?.data ?? {};
    const handleShipperAction = (action, shipper) => {
        if (action === "Make Payment") {
            setSelectedShipper(shipper);
            setMakePaymentModal(true);
        } else if (action === "Edit Shipper") {
            setSelectedShipper(shipper);
            setShowEditShipper(true);
        } else {
            console.log(`${action} for ${shipper.name}`);
        }
    };

    const shipmentShipperOptions = (shipper) => [
        {
            label: "Edit Shipper",
            value: "edit-shipper",
            action: () => {
                if (handleCheckIfCancelled(singleShipment)) {
                    // handleShipperAction("Edit Shipper", singleShipment?.shippingDetails);
                    handleShipperAction("Edit Shipper", shipper);
                }
            },
        },
        // {
        //     label: "Make Payment",
        //     value: "make-payment",
        //     action: () => handleShipperAction("Make Payment", shipper),
        // },
        {
            label: `Mark as ${shipper?.paymentStatus === "UNPAID" ? "Paid" : "Unpaid"}`,
            value: "mark-as-paid",
            className: "text-green-600",
            action: () => {
                if (handleCheckIfCancelled(singleShipment)) {
                    setItemIdToLoad(shipper?._id);
                    markAsPaidFunc({
                        status: shipper?.paymentStatus === "UNPAID" ? "PAID" : "UNPAID",
                        id: shipper?._id,
                    });
                }
            },
        },
    ];

    const handleFetchActivities = (shipmentId) => {
        fetchActivities(`/v1/activity/${shipmentId}`, {}, false);
    };

    useEffect(() => {
        const handleSetConvo = (data) => {
            const connection = typeof data === "string" ? JSON.parse(data) : data;
            setConvoDetails(connection?.connection);
        };
        if (socket) {
            socket.emit(SOCKET_EVENTS.CREATE_CONVERSATION, {
                participants: [singleShipment?.user],
                conversationId: shipmentId,
                conversationType: "Shipment",
            });
            socket.on(SOCKET_LISTENERS?.CONVERSATION_CREATED, handleSetConvo);
        }

        return () => {
            socket?.off(SOCKET_LISTENERS?.CONVERSATION_CREATED, handleSetConvo);
            socket?.off(SOCKET_EVENTS.CREATE_CONVERSATION, handleSetConvo);
        };
    }, [shipmentId]);

    useEffect(() => {
        !shipmentId && navigate("/flex/home/shipments");
        if (business?._id && shipmentId) {
            handleFetchActivities(shipmentId);
        }
    }, [business?._id, shipmentId]);

    const sendMessage = async ({ message, file }) => {
        const formData = new FormData();
        if (message) formData.append("message", message);
        if (file) formData.append("file", file);

        try {
            const response = await axios.post(`/v1/message/send/shipment/${shipmentId}`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });
            return response.data;
        } catch (error) {
            console.error("Error sending message:", error);
            return null;
        }
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case "Payments":
                return <PaymentTab shipmentDetails={singleShipment} />;
            case "Messages":
                return (
                    <Messages
                        sendMessage={sendMessage}
                        businessName={business?.name}
                        orderId={shipmentId}
                        messages={messageList}
                        onSendMessage={setMessageList}
                        socketData={{ socket }}
                        convoDetails={convoDetails}
                    />
                );
            case "Purchase Orders":
                return <PurchaseOrderTab orders={singleShipment?.purchaseOrder} />;
            case "Documents":
                return <DocumentTab documents={singleShipment?.document || []} />;
            case "Activity":
                return <ActivityTab activity={activities} />;
            default:
                return <div className="p-6">Content for {activeTab}</div>;
        }
    };

    const { mutateAsync, isLoading } = useMutation({
        mutationFn: (data) => handleChangeShipmentStatus(data, shipmentId),
        mutationKey: ["changeShipmentStatus", singleShipment?._id],
        onSuccess: (data) => {
            if (!data?.message.includes("successfully")) {
                Swal.fire({
                    text: data?.response?.data?.message || data?.message || "Something went wrong",
                    icon: "error",
                    confirmButtonText: "Close",
                    width: "280px",
                    confirmButtonColor: "crimson",
                });
            } else {
                Swal.fire({
                    title: "Success",
                    text: data?.message,
                    icon: "success",
                    confirmButtonText: "OK",
                    width: "280px",
                    confirmButtonColor: "rgb(111, 0, 255)",
                });
            }
            queryClient.invalidateQueries({ queryKey: ["single-shipment", shipmentId] });
            refetchShipmentData?.();
        },
    });

    //   const handleUpload = async (selectedFile, userGivenName) => {
    //         setUploading(true);
    //         try {
    //             const formData = new FormData();
    //             formData.append("file", selectedFile);

    //             const response = await axios.post("/v1/utility/upload-file", formData, {
    //                 headers: { "Content-Type": "multipart/form-data" },
    //             });

    //             if (response.data?.link) {
    //                 const url = response?.data?.link;
    //                 setFile({
    //                     name: userGivenName,
    //                     url: url,
    //                     file: selectedFile,
    //                 });
    //                 uploadDocFunc({
    //                     link: url,
    //                     name: userGivenName,
    //                     originalFilename: selectedFile?.name,
    //                 });
    //             } else {
    //                 console.error("Upload failed: No file URL returned.");
    //             }
    //         } catch (error) {
    //             console.error("Upload error:", error);
    //         } finally {
    //             setUploading(false);
    //             setShowModal(false);
    //         }
    //   };

    const isReceiverBusiness = business?._id === singleShipment?.receiverBusiness;
    const isActiveBusiness = business?._id === singleShipment?.business;
    const isAssignedViewNotActive =
        isAssignedView && singleShipment?.shipmentStatus === "NotStarted";

    const gettingTheLngAndLat = singleShipment?.shippingDetails?.map((shipper) => ({
        start: {
            lat: shipper?.departurePort?.latitude,
            lng: shipper?.departurePort?.longitude,
            label: shipper?.departurePort?.address,
        },
        end: {
            lat: shipper?.destinationPort?.latitude,
            lng: shipper?.destinationPort?.longitude,
            label: shipper?.destinationPort?.address,
        },
    }));

    return (
        <>
            {addDocs && (
                <UploadDocModal
                    close={() => setAddDocs(false)}
                    uploadFunc={handleUpload}
                    loading={uploading}
                />
            )}
            <CancelShipmentModal
                shipmentDetails={selectedShipment}
                show={cancelShipmentModal}
                onClose={() => setShowCancelShipmentModal(false)}
                refetchData={() => {
                    refetchShipmentData();
                    handleFetchActivities(shipmentId);
                    setShowCancelShipmentModal(false);
                }}
            />
            {recordPaymentModal && (
                <RecordPayment
                    order={selectedShipment}
                    orderId={selectedShipment?._id}
                    totalAmount={selectedShipment?.totalAmount}
                    close={() => setRecordPaymentModal(false)}
                    refetchActivity={() => {
                        refetchShipmentData();
                        handleFetchActivities(shipmentId);
                    }}
                    type="shipment"
                />
            )}
            {showModal && (
                <UpdateStatusModal
                    show={showModal}
                    shipmentId={shipmentId}
                    shipmentDetails={singleShipment?.shippingDetails}
                    refetchData={() => {
                        refetchShipmentData();
                    }}
                    onHide={() => setShowModal(false)}
                />
            )}
            <RejectShipmentModal
                show={showRejectModal}
                setShow={setShowRejectModal}
                shipmentId={shipmentId}
                handleRejectShipment={handleRejectApproveFunc}
            />
            <AcceptShipmentModal
                show={showAcceptModal}
                setShow={setShowAcceptModal}
                shipmentId={shipmentId}
                handleReceiverRejectOrAcceptShipment={handleReceiverRejectOrAcceptFunc}
                shipmentLegs={singleShipment?.shippingDetails}
                isLoading={isLoadingReceiversRejectOrApprove}
            />
            {isLoadingShipment && (
                <div className="fixed left-0 top-0 h-screen w-screen z-[1000000] opacity-65">
                    <Loading />
                </div>
            )}
            <div className="min-h-screen px-3 py-8 sm:px-7">
                <div className="flex !gap-3.5 font-medium text-sm text-main-grey !pb-5 items-center">
                    <ArrowBackRoundIcon
                        className="bg-white rounded-full size-6 !p-1 cursor-pointer"
                        onClick={() => navigate("/flex/home/shipments")}
                    />{" "}
                    Back to shipments
                </div>
                <div className="">{singleShipment?.generatedTitle}</div>

                <div className="flex flex-wrap items-start justify-between mb-6 md:flex-nowrap">
                    <div className="flex items-center gap-3 ">
                        <h1 className="text-2xl font-[450] text-main-dark">
                            {singleShipment?.shipmentTitle || singleShipment?.generatedTitle}
                        </h1>
                        <span
                            style={{
                                backgroundColor:
                                    shipmentStatus[singleShipment?.shipmentStatus]?.bgColor ||
                                    "#DBDFE5",
                                color:
                                    shipmentStatus[singleShipment?.shipmentStatus]?.textColor ||
                                    "#B64607",
                            }}
                            className={`rounded-xl py-1 px-2 w-auto text-xs font-medium`}
                        >
                            {shipmentStatus[singleShipment?.shipmentStatus]?.text}
                        </span>
                    </div>

                    {isAssignedViewNotActive ? (
                        <div className="flex gap-3 mt-2 md:mt-0">
                            <button
                                onClick={() => {
                                    setShowRejectModal(true);
                                }}
                                className="flex px-4 py-2 bg-transparent rounded-lg text-main-dark gap-x-2"
                            >
                                Reject request
                            </button>
                            <button
                                onClick={() => {
                                    setShowAcceptModal(true);
                                }}
                                className="bg-[#6F00FF] flex text-white px-4 py-2 rounded-lg"
                            >
                                Accept request
                            </button>
                        </div>
                    ) : !isAssignedViewNotActive && isReceiverBusiness ? (
                        <div className="flex gap-3 mt-2 md:mt-0">
                            <button
                                onClick={() => {
                                    if (handleCheckIfCancelled(singleShipment)) {
                                        setShowModal(true);
                                    }
                                }}
                                className="bg-[#6F00FF] flex text-white gap-x-2 pl-4 pr-2 rounded-lg"
                            >
                                <span className="py-2">
                                    {singleShipment?.shipmentStatus === "NotStarted"
                                        ? "Update Status..."
                                        : shipmentStatus[singleShipment?.shipmentStatus]?.text}
                                </span>
                                <span className="h-full py-2 pl-2 border-l border-white">
                                    <WhiteArrowDropDown className="text-white" />
                                </span>
                            </button>
                            <button className="flex items-center">
                                {isLoadingReceiversRejectOrApprove ? (
                                    <LuLoader />
                                ) : (
                                    <MoreOptionsDropdown
                                        options={shipmentOptions(singleShipment)}
                                        horizontal
                                    />
                                )}
                            </button>
                        </div>
                    ) : (
                        <div className="flex gap-3 mt-2 md:mt-0">
                            <button
                                onClick={() => setShowModal(true)}
                                className="bg-[#6F00FF] flex text-white gap-x-2 pl-4 pr-2 rounded-lg"
                            >
                                <span className="py-2">
                                    {singleShipment?.shipmentStatus === "NotStarted"
                                        ? "Update Status..."
                                        : shipmentStatus[singleShipment?.shipmentStatus]?.text}
                                </span>
                                <span className="h-full py-2 pl-2 border-l border-white">
                                    <WhiteArrowDropDown className="text-white" />
                                </span>
                            </button>
                            <button className="flex items-center">
                                {loadingShipperFunc ? (
                                    <LuLoader />
                                ) : (
                                    <MoreOptionsDropdown
                                        options={shipmentOptions(singleShipment)}
                                        horizontal
                                    />
                                )}
                            </button>
                        </div>
                    )}
                </div>

                <section className="flex flex-col-reverse gap-6 md:flex-row">
                    {/* left side */}

                    <div className="w-full md:w-7/12">
                        <div>
                            <p className="font-medium text-sm text-gray-40 !mb-2">Item details</p>
                            <div className="bg-white rounded-lg !px-3.5 !py-3">
                                <p className="text-sm font-medium text-gray-40">
                                    {singleShipment?.containerType} {singleShipment?.loadType}
                                </p>
                                {singleShipment?.description && (
                                    <p className="mt-1 text-sm font-normal text-gray-10">
                                        {singleShipment?.description}
                                    </p>
                                )}
                                <div className="grid grid-cols-2 gap-6 mt-6 md:grid-cols-3">
                                    <div>
                                        <p className="text-xs text-main-grey">Total Weight</p>
                                        <p className="text-sm font-medium text-main-dark">
                                            {singleShipment?.totalWeight} Kg
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-main-grey">Quantity</p>
                                        <p className="text-sm font-medium text-main-dark">
                                            {singleShipment?.totalQuantity} {singleShipment?.packageType}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-main-grey">Package Dimensions</p>
                                        <p className="text-sm font-medium text-main-dark">
                                            {singleShipment?.packageDimention?.length} cm x{" "}
                                            {singleShipment?.packageDimention?.width} cm x{" "}
                                            {singleShipment?.packageDimention?.height} cm
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {singleShipment?.shippingDetails?.length ? (
                            <>
                                <p className="pb-1 mt-5 font-normal">Shippers</p>
                                <div className="mb-6 bg-white rounded-xl">
                                    {singleShipment?.shippingDetails?.map((shipper, index) => (
                                        <div
                                            key={index}
                                            className="flex justify-between items-center !px-5 !py-4"
                                        >
                                            <div className="flex items-center justify-start gap-3">
                                                <div className="flex items-center justify-center w-10 h-10 font-semibold text-[#475467] bg-[#EFF0FB] rounded-full">
                                                    {getInitialsMessage(shipper?.agentName)}
                                                </div>
                                                <div className="text-left">
                                                    <h3 className="text-lg font-medium mb-0 !text-left capitalize text-gray-60">
                                                        {shipper?.agentName}
                                                    </h3>
                                                    <p
                                                        style={{ letterSpacing: "-3.5%" }}
                                                        className="text-xs font-light text-[#667085]"
                                                    >
                                                        {shipper?.type === "Truck"
                                                            ? " Trucker"
                                                            : "Clearing agent"}{" "}
                                                        • from{" "}
                                                        {shipper?.departurePort?.address
                                                            ? shipper?.departurePort?.address
                                                            : shipper?.departurePort}{" "}
                                                        to{" "}
                                                        {shipper?.destinationPort?.address
                                                            ? shipper?.destinationPort?.address
                                                            : shipper?.destinationPort}
                                                    </p>
                                                </div>
                                            </div>
                                            {loadingShipperFunc && itemIdToLoad === shipper?._id ? (
                                                <LuLoader />
                                            ) : // isAssignedViewNotActive && isReceiverBusiness ? <div>Pending</div> : <p
                                            // isAssignedViewNotActive ? (
                                            isAssignedViewNotActive && isReceiverBusiness ? (
                                                <div className="flex items-center gap-x-2 text-[#DB6704]">
                                                    <p>Pending</p> <LoadingIcon />
                                                </div>
                                            ) : (
                                                <p
                                                    className={`text-sm flex items-center gap-1 font-semibold capitalize ${
                                                        shipper.paymentStatus !== "UNPAID"
                                                            ? "text-green-600"
                                                            : "text-main-dark"
                                                    }`}
                                                >
                                                    {shipper.paymentStatus === "UNPAID"
                                                        ? "Unpaid"
                                                        : "Paid"}{" "}
                                                    {shipper.paymentStatus !== "UNPAID" ? (
                                                        <CircleGreenCircle />
                                                    ) : null}
                                                    <MoreOptionsDropdown
                                                        options={shipmentShipperOptions(shipper)}
                                                    />
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : null}

                        <div className="mt-3">
                            {/* Tabs */}
                            <div className="flex !mt-7 gap-4 md:!gap-8 px-3 md:!px-6 overflow-auto  [scrollbar-width:none] md:overflow-hidden">
                                {tabs.map((tab, idx) => (
                                    <span
                                        className={cn(
                                            `!pb-2 !border-b-2 font-normal cursor-pointer transition-all whitespace-nowrap duration-200`,
                                            activeTab === tab
                                                ? "font-[450] border-secondary text-main-dark"
                                                : "!border-transparent text-main-grey",
                                            idx === 3 ? "flex items-center gap-x-1" : ""
                                        )}
                                        style={{
                                            fontWeight: activeTab === tab ? "450" : "400",
                                            borderColor:
                                                activeTab === tab
                                                    ? "#6F00FF !important"
                                                    : "transparent !important",
                                            color:
                                                activeTab === tab
                                                    ? "text-main-dark"
                                                    : "text-main-grey",
                                            borderBottom: "2px solid !important",
                                        }}
                                        key={tab}
                                        onClick={() => setActiveTab(tab)}
                                    >
                                        {tab}
                                        {/* {idx === 3 && (
                                            <span className="size-4 rounded-full bg-[#11A811] text-xs text-[#FCFCFD] grid place-items-center">
                                                4
                                            </span>
                                        )} */}
                                    </span>
                                ))}
                            </div>

                            {/* Tab Content */}
                            <div
                                className={`bg-white rounded-lg min-h-[400px] ${
                                    activeTab === "Messages" ? "" : "p-6"
                                }`}
                            >
                                {renderTabContent()}
                            </div>
                        </div>
                    </div>

                    {/* right side */}
                    <div className="w-full md:w-5/12">
                        <p className="mb-2 text-sm font-medium text-gray-40">Track Shipment</p>
                        <div className="mb-6 rounded-xl ">
                            <WorldMap dots={gettingTheLngAndLat} />
                            {/* status tracking shhould be done here */}
                            <StatusTracking
                                singleShipmentStatus={singleShipment?.shippingDetails}
                                shipmentDetails={singleShipment}
                            />
                        </div>
                        {/* map section */}
                    </div>
                </section>
                {makePaymentModal && (
                    <MakePaymentModal
                        currency={singleShipment?.currency}
              wallets={business?._id}
                        orderId={shipmentId}
                        close={() => setMakePaymentModal(false)}
                    />
                )}

                {showEditShipper && selectedShipper && (
                    <EditShipperModal
                        show={showEditShipper}
                        shippers={[selectedShipper]}
                        onUpdate={() => {
                            setShowEditShipper(false);
                        }}
                        refetchData={() => {
                            refetchShipmentData();
                        }}
                        shipmentId={shipmentId}
                        onClose={() => setShowEditShipper(false)}
                    />
                )}
            </div>
            <RatingModal
                isOpen={showRating}
                onClose={() => setShowRating(false)}
                userId={business?._id}
                reviewType="Shipment"
                title="How was your experience creating a new shipment?"
                subtitle="We’d like to know how satisfied you are with the process"
                firebaseEventName="shipment_feedback_rating"
                featureVersion="shipment_v1"
            />
        </>
    );
};

export default ShipmentShow;
